import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControlLabel, IconButton, InputAdornment,
  LinearProgress, MenuItem, Pagination, Paper, Select, Stack, Switch, TextField, Tooltip,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import MovieIcon from '@mui/icons-material/Movie';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import {
  describirConversion, formatearVelocidad, urlPoster, useEventos,
} from '../lib/eventos.js';
import { describirEstado } from '../lib/estados.js';
import { useColaConversion } from '../components/ColaConversion.js';
import { DialogoFicha } from '../components/DialogoFicha.js';
import { DialogoVincular } from '../components/DialogoVincular.js';
import { InsigniaBiblioteca } from '../components/InsigniaBiblioteca.js';
import type { ApiFallo, DescargaDeWatchlist, ElementoWatchlist, ItemCola } from '../lib/api.js';

const CALIDADES = ['480p', '720p', '1080p', '2160p'] as const;

/** Los filtros de la lista. Los tres primeros van sobre la biblioteca, y son tres y no dos
 *  porque «sin saber» no es lo mismo que «no la tengo»: sin bibliotecas de Plex marcadas la
 *  app no puede responder, y esconder eso detrás de «no la tengo» invitaría a descargar cosas
 *  que ya tienes.
 *
 *  El cuarto es de otro eje —tener ficha de HispaShare o no—, y está porque es lo único que
 *  se puede arreglar a mano: sin ficha no hay versiones que mirar, así que esas filas no
 *  avanzan solas por mucho que pasen los barridos. */
type Filtro = 'todas' | 'en-plex' | 'faltan' | 'sin-vincular';

const FILTROS: Record<Filtro, { etiqueta: string; cumple: (e: ElementoWatchlist) => boolean }> = {
  todas: { etiqueta: 'Todas', cumple: () => true },
  'en-plex': { etiqueta: 'Ya en Plex', cumple: (e) => e.library.status === 'owned' },
  faltan: { etiqueta: 'Me faltan', cumple: (e) => e.library.status === 'missing' },
  'sin-vincular': { etiqueta: 'Sin vincular', cumple: (e) => e.hispashareId === null },
};

const CLAVES_FILTRO = Object.keys(FILTROS) as Filtro[];

/** Cuántas filas se pintan de una vez. Cada fila se suscribe al progreso en vivo, y una
 *  watchlist de mil películas pintada entera saturaba el móvil solo con desplazarse. */
const TAMANOS_PAGINA = [25, 50, 100] as const;

/** Sin acentos, sin mayúsculas y sin espacios de sobra: «senor» tiene que encontrar «Señor,
 *  dame paciencia», porque la lista viene de Plex y no se recuerda cómo la escribieron. */
function normalizarTexto(t: string): string {
  return t
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Si un elemento casa con lo tecleado en el buscador de la lista. Se mira el título, el
 *  texto con que se añadió (las manuales no tienen título hasta vincularse) y el año, y
 *  cada palabra del término tiene que aparecer en alguno: «padrino 1974» encuentra la
 *  segunda parte sin que importe el orden. Con el término vacío casa todo. */
function coincideConBusqueda(e: ElementoWatchlist, termino: string): boolean {
  const palabras = normalizarTexto(termino).split(' ').filter(Boolean);
  if (palabras.length === 0) return true;
  const pajar = normalizarTexto([e.title, e.query, e.year].filter(Boolean).join(' '));
  return palabras.every((palabra) => pajar.includes(palabra));
}

function describir(e: ElementoWatchlist): { texto: string; color: 'default' | 'info' | 'success' | 'warning' } {
  switch (e.status) {
    case 'pending':
      return { texto: 'Vigilando', color: 'default' };
    case 'found':
      return { texto: 'Disponible', color: 'warning' };
    case 'downloading':
      return { texto: 'Descargando', color: 'info' };
    case 'done':
      return { texto: 'Conseguida', color: 'success' };
    case 'abandoned':
      return { texto: 'Abandonada', color: 'default' };
  }
}

/** Dónde se sigue una película según el punto en el que esté. Una que espera en
 *  conversiones no se atiende en Descargas: el enlace tiene que llevar a donde está el
 *  botón que hace falta —elegir biblioteca— y no a la lista donde solo se mira. */
function dondeSeSigue(status: DescargaDeWatchlist['status']): string {
  return status === 'ready_to_place' || status === 'importing' || status === 'import_failed'
    ? '/conversiones'
    : '/descargas';
}

/** Una fila de la lista.
 *
 *  Es un componente y no un trozo del `map` porque el progreso en vivo se lee con hooks y
 *  hay que leer SOLO el de esta película: seleccionar el mapa entero repinta la lista
 *  completa cada vez que llega un evento de cualquiera de ellas, y llegan cada segundo. */
function FilaWatchlist({
  e, enCola, onAbrir, onVincular, onAutomatica, onBorrar,
}: {
  e: ElementoWatchlist;
  enCola?: ItemCola;
  onAbrir: () => void;
  onVincular: () => void;
  onAutomatica: (valor: boolean) => void;
  onBorrar: () => void;
}) {
  const d = e.descarga;
  // Por hash el de aMule, por id de descarga el de la conversión: son dos barras distintas
  // de la misma película y mientras se convierte existen las dos.
  const vivo = useEventos((s) => (d?.ed2kHash ? s.progreso[d.ed2kHash] : undefined));
  const progresoConversion = useEventos((s) => (d ? s.progresoConversion[d.id] : undefined));

  // Con descarga manda lo que le pasa al FICHERO; sin ella, lo que dice la vigilancia. Y
  // las palabras son literalmente las de Descargas: que la misma película se llame «En
  // conversiones» aquí y allí es la mitad del propósito de enseñarlo.
  const estado: { texto: string; color: 'default' | 'info' | 'success' | 'warning' | 'error'; nota?: string } =
    d ? describirEstado(d, enCola, progresoConversion) : describir(e);

  const bajando =
    d && (d.status === 'queued' || d.status === 'downloading' || d.status === 'paused' ||
      d.status === 'hashing');
  const convirtiendo = d?.status === 'transcoding' && enCola?.estado !== 'en_espera';
  const fase = describirConversion(progresoConversion);
  const pct = bajando ? (vivo?.progressPct ?? d.progressPct) : fase.porcentaje;

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' } }}>
        {/* Toda la zona del título es clicable, no solo el texto: es lo que se
            intenta pulsar por instinto. Los controles de la derecha quedan fuera
            para que no disparen la navegación al usarlos. */}
        <Stack
          direction="row"
          spacing={2}
          onClick={onAbrir}
          sx={{
            flex: 1,
            minWidth: 0,
            alignItems: 'center',
            cursor: 'pointer',
            borderRadius: 1,
            p: 0.5,
            m: -0.5,
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          {/* Hueco de tamaño fijo aunque no haya carátula: si no, las filas sin
              vincular bailarían respecto a las demás. */}
          <Box
            sx={{
              width: 46,
              minWidth: 46,
              aspectRatio: '2/3',
              borderRadius: 1,
              overflow: 'hidden',
              bgcolor: 'action.disabledBackground',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            {urlPoster(e.posterUrl) ? (
              <Box
                component="img"
                src={urlPoster(e.posterUrl)!}
                alt=""
                loading="lazy"
                sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              <MovieIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            )}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {e.title ?? e.query}
              {e.year && (
                <Typography component="span" color="text.secondary">
                  {' '}
                  ({e.year})
                </Typography>
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {[
                e.hispashareId ? null : 'sin vincular todavía',
                e.minQuality ? `mínimo ${e.minQuality}` : null,
                e.requiredLangs.length ? `audio ${e.requiredLangs.join('/')}` : null,
                e.lastCheckedAt
                  ? `revisada ${new Date(e.lastCheckedAt).toLocaleString('es-ES')}`
                  : 'aún sin revisar',
              ]
                .filter(Boolean)
                .join(' · ')}
            </Typography>

            {/* La barra solo aparece cuando hay algo moviéndose. Esperando turno en la cola
                de conversión NO lo hay: el puesto lo dice el chip, y una barra ahí haría
                creer que ffmpeg ya está trabajando en esta película. */}
            {(bajando || convirtiendo) && (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 0.5 }}>
                <LinearProgress
                  // Sin cifra —analizando, leyendo subtítulos por OCR— la barra indefinida
                  // dice «está trabajando» sin inventarse un número.
                  variant={pct == null ? 'indeterminate' : 'determinate'}
                  value={pct ?? 0}
                  sx={{ flex: 1, minWidth: 80 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {pct == null ? '—' : `${pct.toFixed(0)} %`}
                  {bajando && ` · ${formatearVelocidad(vivo?.speedBps ?? d.speedBps)}`}
                </Typography>
              </Stack>
            )}

            {estado.nota && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {estado.nota}
              </Typography>
            )}
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <InsigniaBiblioteca estado={e.library} />
          {/* Con descarga el chip es un enlace: la pregunta que sigue a «se está
              convirtiendo» es siempre «enséñamela», y sin esto había que buscarla a mano
              por el título en la otra pantalla. */}
          {d ? (
            <Chip
              size="small"
              color={estado.color}
              label={estado.texto}
              component={Link}
              to={dondeSeSigue(d.status)}
              clickable
            />
          ) : (
            <Chip size="small" color={estado.color} label={estado.texto} />
          )}
          {e.source === 'plex' && (
            <Tooltip title="Vino de tu watchlist de Plex. Quitarla de Plex no la borra de aquí.">
              <Chip size="small" variant="outlined" label="Plex" />
            </Tooltip>
          )}
          {/* Una serie se encuentra y se propone; el interruptor de automática no le hace
              nada, y ofrecerlo sería prometer lo que no va a pasar (D-27). */}
          {e.kind === 'show' ? (
            <Tooltip title="Es una serie: cuando el barrido la encuentra, avisa. Qué temporadas bajar lo eliges tú en su ficha.">
              <Chip size="small" color="primary" label="Serie" />
            </Tooltip>
          ) : (
          <Tooltip title="Apagado, solo avisa. Encendido, descarga sin preguntar.">
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={e.autoDownload}
                  onChange={(ev) => onAutomatica(ev.target.checked)}
                />
              }
              label={<Typography variant="caption">Automática</Typography>}
            />
          </Tooltip>
          )}
          {/* El escape del cruce automático, en la fila y no escondido en un menú: cuando
              falla, falla en UNA película concreta y es esta fila la que lo está diciendo. */}
          <Tooltip
            title={
              e.hispashareId
                ? 'Está vinculada a una ficha. Púlsalo si es la equivocada.'
                : 'Buscar su ficha en HispaShare y vincularla a mano'
            }
          >
            <IconButton size="small" color={e.hispashareId ? 'default' : 'primary'} onClick={onVincular}>
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onBorrar}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
    </Paper>
  );
}

export function Watchlist() {
  const qc = useQueryClient();
  const navegar = useNavigate();
  const [texto, setTexto] = useState('');
  const [calidad, setCalidad] = useState<string>('1080p');
  const [automatica, setAutomatica] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ['watchlist'], queryFn: api.watchlist });
  // La misma consulta compartida que usan Descargas y Conversiones: el puesto en la cola
  // no está en la base de datos, solo en la memoria de la API.
  const cola = useColaConversion().data?.items ?? [];
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['watchlist'] });

  const anadir = useMutation({
    mutationFn: () =>
      api.anadirWatchlist({
        query: texto.trim(),
        minQuality: calidad || null,
        requiredLangs: ['es'],
        autoDownload: automatica,
      }),
    onSuccess: () => {
      setTexto('');
      refrescar();
    },
  });

  const cambiar = useMutation({
    mutationFn: ({ id, cambios }: { id: string; cambios: Record<string, unknown> }) =>
      api.actualizarWatchlist(id, cambios),
    onSuccess: refrescar,
  });

  const borrar = useMutation({ mutationFn: api.borrarWatchlist, onSuccess: refrescar });

  const importar = useMutation({
    mutationFn: api.importarDePlex,
    onSuccess: () => {
      refrescar();
      void qc.invalidateQueries({ queryKey: ['avisos'] });
    },
  });

  /** Lo que el SERVIDOR está haciendo. Se pregunta al montar la pantalla, así que volver
   *  aquí después de haberse ido enseña por dónde va en vez de haber perdido el hilo; y
   *  mientras corre se refresca sola. */
  const tarea = useQuery({
    queryKey: ['watchlist-tarea'],
    queryFn: api.tareaWatchlist,
    refetchInterval: (q) => (q.state.data?.task?.enCurso ? 1500 : false),
  });
  const enCurso = tarea.data?.task?.enCurso ? tarea.data.task : null;
  /** La última que terminó, para enseñar cómo acabó aunque no estuvieras delante. */
  const hecha = tarea.data?.task && !tarea.data.task.enCurso ? tarea.data.task : null;
  /** El elemento cuyo diálogo de vinculación a mano está abierto. Se monta con él dentro, así
   *  que cada apertura empieza con el formulario relleno de ESA película. */
  const [aVincular, setAVincular] = useState<ElementoWatchlist | null>(null);
  /** El elemento cuya ficha está abierta en el diálogo. En un diálogo y no en su página
   *  porque volver de la página tiraba la página del paginador, el filtro y la búsqueda:
   *  con mil películas, cada descarga era volver a encontrar la fila. */
  const [fichaAbierta, setFichaAbierta] = useState<ElementoWatchlist | null>(null);

  /** Las dos tareas largas se piden y ya: el trabajo lo hace el servidor por tandas, y aquí
   *  solo se refresca el estado. Si ya hay una corriendo, el servidor devuelve la suya y no
   *  arranca otra. */
  const lanzar = useMutation({
    mutationFn: (cual: 'barrido' | 'barrido-todas' | 'vinculacion') =>
      cual === 'vinculacion' ? api.vincularFichas() : api.barrerWatchlist(cual === 'barrido-todas'),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['watchlist-tarea'] }),
  });

  // Cuando una tarea termina, lo que hay en pantalla ya no vale: la lista ha cambiado y
  // puede haber avisos nuevos.
  useEffect(() => {
    if (tarea.data?.task && !tarea.data.task.enCurso) {
      refrescar();
      void qc.invalidateQueries({ queryKey: ['avisos'] });
    }
    // Depende solo del dato: lo que dispara esto es que llegue un estado ya terminado.
  }, [tarea.data?.task?.terminada]);

  const todos = data?.items ?? [];

  const [filtro, setFiltro] = useState<Filtro>('faltan');
  /** Lo tecleado en el buscador de la lista. Va ANTES de los filtros: cada pestaña enseña
   *  y cuenta solo lo que casa con el término, así que el número de cada chip dice cuántas
   *  de las encontradas están en Plex, faltan o siguen sin ficha. */
  const [busqueda, setBusqueda] = useState('');
  const buscando = busqueda.trim() !== '';
  const encontrados = buscando ? todos.filter((e) => coincideConBusqueda(e, busqueda)) : todos;
  const items = encontrados.filter(FILTROS[filtro].cumple);
  const cuenta = Object.fromEntries(
    CLAVES_FILTRO.map((clave) => [clave, encontrados.filter(FILTROS[clave].cumple).length]),
  ) as Record<Filtro, number>;

  /** Paginación en el navegador, sobre lo ya filtrado y buscado. La lista entera viaja
   *  igual —los contadores de los chips la necesitan—; lo que ahoga al dispositivo es
   *  PINTAR mil filas, no recibirlas. */
  const [porPagina, setPorPagina] = useState<number>(TAMANOS_PAGINA[0]);
  const [pagina, setPagina] = useState(1);
  const totalPaginas = Math.max(1, Math.ceil(items.length / porPagina));
  // Acotada, no reseteada: si la lista mengua por debajo de la página actual se enseña la
  // última que queda, sin un repintado con la página de más.
  const paginaActual = Math.min(pagina, totalPaginas);
  const visibles = items.slice((paginaActual - 1) * porPagina, paginaActual * porPagina);
  const paginado = items.length > TAMANOS_PAGINA[0];
  /** El bloque de chips: al cambiar de página se vuelve ahí arriba, que es donde empieza
   *  la lista, y no al principio de la pantalla con el formulario de alta. */
  const cabeceraLista = useRef<HTMLDivElement>(null);
  const irAPagina = (n: number) => {
    setPagina(n);
    cabeceraLista.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  };
  // Cambiar de pestaña o de término es otra lista: se empieza por su primera página. Ir
  // a parar a la página 3 de «Ya en Plex» porque se estaba en la 3 de «Todas» despista.
  useEffect(() => {
    setPagina(1);
  }, [filtro, busqueda, porPagina]);
  /** Las que no se sabe si están: sin bibliotecas marcadas no caben ni en «Ya en Plex» ni en
   *  «Me faltan», y arrancando en «Me faltan» eso sería una lista vacía sin explicación. */
  const sinSaber = todos.filter((e) => e.library.status === 'unknown').length;

  /** Si ya conocemos su ficha, se abre aquí mismo, encima de la lista. Si no —el caso de
   *  todo lo recién importado de Plex, que aún no se ha buscado— se lleva a la búsqueda
   *  con el título puesto, que es exactamente lo que harías a mano. */
  const abrir = (e: ElementoWatchlist) => {
    if (e.hispashareId) setFichaAbierta(e);
    // Respaldo mientras no esté vinculada: la búsqueda con el título puesto, en su pestaña.
    else navegar(`/buscar?q=${encodeURIComponent(e.title ?? e.query ?? '')}${e.kind === 'show' ? '&tipo=series' : ''}`);
  };

  // Sobre `todos` y no sobre lo filtrado ni lo buscado: «Vincular ahora» las revisa TODAS
  // en el servidor, así que contar solo las visibles dejaba la barra en «12 de 5…».
  const sinVincular = todos.filter(FILTROS['sin-vincular'].cumple).length;

  return (
    <Stack spacing={3}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5">Watchlist</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Trae las películas y las series de tu watchlist de Plex. Las series se encuentran y se proponen; nunca se bajan solas.">
            <span>
              <Button size="small" disabled={importar.isPending} onClick={() => importar.mutate()}>
                {importar.isPending ? 'Importando…' : 'Importar de Plex'}
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            disabled={!!enCurso || lanzar.isPending}
            onClick={() => lanzar.mutate('barrido')}
          >
            {enCurso?.nombre === 'barrido' ? 'Buscando…' : 'Buscar ahora'}
          </Button>
          {/* Lo normal es mirar solo lo nuevo y lo que lleva una semana sin revisarse. Esto
              vuelve a mirarlo TODO, que es lo que hace falta para repetir una simulación
              entera —y son mil peticiones, así que va en su propio botón. */}
          <Tooltip title="Vuelve a evaluar toda la lista, incluidas las ya revisadas. Tarda: una petición por segundo a HispaShare.">
            <span>
              <Button
                size="small"
                disabled={!!enCurso || lanzar.isPending}
                onClick={() => lanzar.mutate('barrido-todas')}
              >
                Revisar todo
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2 }}>
        <Stack
          component="form"
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ alignItems: { md: 'center' } }}
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            if (texto.trim()) anadir.mutate();
          }}
        >
          <TextField
            size="small"
            label="Película que quieres"
            placeholder="Título…"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Select size="small" value={calidad} onChange={(e) => setCalidad(e.target.value)} sx={{ minWidth: 130 }}>
            {CALIDADES.map((c) => (
              <MenuItem key={c} value={c}>
                Mínimo {c}
              </MenuItem>
            ))}
          </Select>
          <Tooltip title="Apagado, solo te avisa cuando aparezca. Encendido, la descarga sin preguntar.">
            <FormControlLabel
              control={<Switch checked={automatica} onChange={(e) => setAutomatica(e.target.checked)} />}
              label="Descargar sola"
            />
          </Tooltip>
          <Button type="submit" variant="contained" disabled={!texto.trim() || anadir.isPending}>
            Añadir
          </Button>
        </Stack>

        {anadir.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(anadir.error as ApiFallo).message}
          </Alert>
        )}
      </Paper>

      {sinVincular > 0 && (
        <Alert
          severity="info"
          action={
            enCurso?.nombre === 'vinculacion' ? (
              <Typography variant="caption" sx={{ pr: 1 }}>
                {enCurso.hechos} de {enCurso.total || sinVincular}…
              </Typography>
            ) : (
              <Stack direction="row" spacing={1}>
                {/* Cuáles son, antes de decidir nada: el reintento automático no las va a
                    sacar todas y las que queden hay que vincularlas a mano una a una. */}
                {filtro !== 'sin-vincular' && (
                  <Button size="small" onClick={() => setFiltro('sin-vincular')}>
                    Ver cuáles
                  </Button>
                )}
                <Button size="small" disabled={!!enCurso} onClick={() => lanzar.mutate('vinculacion')}>
                  Vincular ahora
                </Button>
              </Stack>
            )
          }
        >
          {sinVincular} película(s) sin ficha de HispaShare. Se vinculan comparando el IMDb,
          no el título — tu watchlist está en inglés y las fichas en castellano. Las que no
          salgan se vinculan a mano, con el botón de la cadena de cada fila.
          {enCurso?.nombre === 'vinculacion' && <LinearProgress sx={{ mt: 1 }} />}
        </Alert>
      )}

      {importar.data && (
        <Alert severity={importar.data.nuevos ? 'success' : 'info'} onClose={() => importar.reset()}>
          De {importar.data.leidos} elementos en Plex ({importar.data.peliculas} películas y{' '}
          {importar.data.series} series): {importar.data.nuevos} nuevos,{' '}
          {importar.data.yaEstaban} ya estaban
          {importar.data.retituladas > 0 ? ` (${importar.data.retituladas} con título nuevo)` : ''}
          {importar.data.sinImdb > 0 ? `, ${importar.data.sinImdb} sin IMDb` : ''}.
        </Alert>
      )}

      {importar.isError && (
        <Alert severity="error" onClose={() => importar.reset()}>
          {(importar.error as ApiFallo).message}
        </Alert>
      )}

      {/* Lo que está pasando AHORA, con su fase: un barrido de mil películas tarda minutos
          y sin esto es indistinguible de uno colgado. Sigue ahí si te vas y vuelves. */}
      {enCurso && (
        <Alert severity="info" icon={false}>
          <Stack spacing={1}>
            <Typography variant="body2">
              {enCurso.nombre === 'barrido' ? 'Buscando en HispaShare' : 'Vinculando fichas'}
              {' · '}{enCurso.fase}
              {enCurso.total > 0 && ` · ${enCurso.hechos} de ${enCurso.total}`}
            </Typography>
            <LinearProgress
              variant={enCurso.total > 0 ? 'determinate' : 'indeterminate'}
              value={enCurso.total > 0 ? Math.min(100, (enCurso.hechos / enCurso.total) * 100) : undefined}
            />
            <Typography variant="caption" color="text.secondary">
              Sigue en el servidor aunque cierres esta pestaña.
            </Typography>
          </Stack>
        </Alert>
      )}

      {hecha?.resumen && (
        <Alert severity={hecha.error ? 'error' : 'success'}>
          {hecha.nombre === 'barrido' && hecha.resumen
            ? `Revisadas ${hecha.resumen.revisados}: ${hecha.resumen.encontrados} disponibles, ` +
              `${hecha.resumen.encolados} encoladas` +
              (hecha.resumen.simuladas ? `, ${hecha.resumen.simuladas} simuladas` : '') + '.'
            : hecha.nombre === 'vinculacion' && hecha.resumen
              ? `Vinculadas ${hecha.resumen.vinculados} de ${hecha.resumen.revisados} revisadas.`
              : ''}
          {hecha.resumen.errores.length > 0 && ` ${hecha.resumen.errores.length} con problemas.`}
          {hecha.error && ` ${hecha.error}`}
        </Alert>
      )}

      <Stack
        ref={cabeceraLista}
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ alignItems: { sm: 'center' }, flexWrap: 'wrap', scrollMarginTop: 16 }}
        useFlexGap
      >
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', flex: 1 }} useFlexGap>
          {CLAVES_FILTRO.map((clave) => (
            <Chip
              key={clave}
              label={`${FILTROS[clave].etiqueta} (${cuenta[clave]})`}
              color={filtro === clave ? 'primary' : 'default'}
              variant={filtro === clave ? 'filled' : 'outlined'}
              onClick={() => setFiltro(clave)}
            />
          ))}
        </Stack>
        {/* Busca en la lista, no en HispaShare: es para encontrar una película entre mil,
            y los números de los chips cambian con ella. */}
        <TextField
          size="small"
          placeholder="Buscar en la lista…"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          sx={{ minWidth: { sm: 260 } }}
          slotProps={{
            htmlInput: { 'aria-label': 'Buscar en la lista' },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: buscando ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Borrar búsqueda" onClick={() => setBusqueda('')}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
      </Stack>

      {isLoading && <CircularProgress size={28} />}

      {!isLoading && todos.length === 0 && (
        <Alert severity="info">
          La watchlist está vacía. Añade una película y se revisará sola cada 6 horas.
        </Alert>
      )}

      {/* Con un término puesto la lista vacía es «nada casa», no «no falta ninguna»: los
          textos de abajo hablan del estado de la watchlist entera y aquí mentirían. */}
      {!isLoading && todos.length > 0 && items.length === 0 && buscando && (
        <Alert
          severity="info"
          action={
            <Stack direction="row" spacing={1}>
              {encontrados.length > 0 && filtro !== 'todas' && (
                <Button size="small" onClick={() => setFiltro('todas')}>
                  Ver todas
                </Button>
              )}
              <Button size="small" onClick={() => setBusqueda('')}>
                Quitar búsqueda
              </Button>
            </Stack>
          }
        >
          {encontrados.length === 0
            ? `Ninguna película de la watchlist casa con «${busqueda.trim()}».`
            : encontrados.length === 1
              ? `La única que casa con «${busqueda.trim()}» no está en «${FILTROS[filtro].etiqueta}».`
              : `Ninguna de las ${encontrados.length} que casan con «${busqueda.trim()}» está en ` +
                `«${FILTROS[filtro].etiqueta}».`}
        </Alert>
      )}

      {!isLoading && todos.length > 0 && items.length === 0 && !buscando && (
        <Alert
          severity="info"
          action={
            <Button size="small" onClick={() => setFiltro('todas')}>
              Ver todas
            </Button>
          }
        >
          {filtro === 'sin-vincular'
            ? 'Todas tienen ya su ficha de HispaShare. Las que se queden sin ella aparecerán aquí.'
            : filtro === 'en-plex'
              ? 'Ninguna de tu watchlist está todavía en Plex.'
              : sinSaber === todos.length
                ? 'No hay ninguna biblioteca de Plex marcada, así que la app no puede saber cuáles ' +
                  'te faltan. Se marca en Ajustes; mientras tanto, están todas en «Todas».'
                : 'No falta ninguna: las que vigilas están ya en Plex.'}
        </Alert>
      )}

      {visibles.map((e) => (
        <FilaWatchlist
          key={e.id}
          e={e}
          enCola={cola.find((c) => c.downloadId === e.descarga?.id)}
          onAbrir={() => abrir(e)}
          onVincular={() => setAVincular(e)}
          onAutomatica={(v) => cambiar.mutate({ id: e.id, cambios: { autoDownload: v } })}
          onBorrar={() => borrar.mutate(e.id)}
        />
      ))}

      {/* Solo cuando hay más de una página que enseñar con el tamaño más pequeño: con
          cuatro películas un paginador es ruido. */}
      {paginado && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
          useFlexGap
        >
          <Typography variant="caption" color="text.secondary">
            {(paginaActual - 1) * porPagina + 1}–{Math.min(paginaActual * porPagina, items.length)} de {items.length}
          </Typography>
          <Pagination
            count={totalPaginas}
            page={paginaActual}
            onChange={(_ev, n) => irAPagina(n)}
            color="primary"
            siblingCount={0}
            boundaryCount={1}
          />
          <Select
            size="small"
            value={porPagina}
            onChange={(e) => setPorPagina(Number(e.target.value))}
            inputProps={{ 'aria-label': 'Filas por página' }}
          >
            {TAMANOS_PAGINA.map((n) => (
              <MenuItem key={n} value={n}>
                {n} por página
              </MenuItem>
            ))}
          </Select>
        </Stack>
      )}

      {aVincular && (
        <DialogoVincular
          item={{ titulo: aVincular.title ?? aVincular.query ?? '', anio: aVincular.year, hispashareId: aVincular.hispashareId }}
          vincular={async (hispashareId) => {
            const r = await api.vincularAMano(aVincular.id, hispashareId);
            void qc.invalidateQueries({ queryKey: ['watchlist'] });
            return { imdbDiscrepa: r.imdbDiscrepa, titulo: r.item.title ?? '', imdbId: r.item.imdbId ?? '' };
          }}
          onCerrar={() => setAVincular(null)}
        />
      )}
      {fichaAbierta?.hispashareId != null && (
        <DialogoFicha
          // La ruta lo lleva como texto y la consulta de la ficha también.
          id={String(fichaAbierta.hispashareId)}
          kind={fichaAbierta.kind}
          onCerrar={() => setFichaAbierta(null)}
        />
      )}
    </Stack>
  );
}
